import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
} from '@mui/material';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import {
  DataGridPro,
  GridColDef,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../v2/general/CustomDataGrid';
import type { VirtualElement } from '@popperjs/core';
import CustomButton from '../v2/general/CustomButton/CustomButton';
import CustomColumnMenu, {
  columnMenuPopperSx,
} from '../v2/general/CustomDataGrid/CustomColumnMenu';
import { useColumnOrder } from './useColumnOrder';
import vehiclesMock from '../../data/mockedData/schedules/vehicles.json';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../store/ModalProvider';
import type {
  DriverServiceTotalsModalData,
  DriverSituationModalRow,
  DriverSituationsModalData,
} from '../../types/modal';

const vehicleMenuItems = [
  'Ficha do Motorista',
  'Folha de Ponto Virtual Autom\u00e1tica',
  'Situa\u00e7\u00f5es do Motorista',
  'Servi\u00e7os e Totais por Motorista',
];

const driverSituationsRows: DriverSituationModalRow[] = [
  {
    id: '1',
    inicio: 'sex 10/05/2024',
    fim: 'sex 10/05/2024',
    dias: '0',
    idSituacao: '3',
    situacao: 'Folga Atrasada',
    descricao: 'Lorem Ipsum',
    idRegisto: '73252',
    idRegistoOk: '000073252',
    utilizador: 'Rita Cisneiros',
    dataRegisto: 'qui 09/05/2024',
  },
  {
    id: '2',
    inicio: 's\u00e1b 01/06/2024',
    fim: 'sex 01/07/2024',
    dias: '28',
    idSituacao: '9',
    situacao: 'Outra Situa\u00e7\u00e3o',
    descricao: '\u2014',
    idRegisto: '73182',
    idRegistoOk: '000073182',
    utilizador: 'Rita Cisneiros',
    dataRegisto: 'qui 09/05/2024',
  },
  {
    id: '3',
    inicio: 's\u00e1b 02/07/2024',
    fim: 'sex 03/08/2024',
    dias: '31',
    idSituacao: '9',
    situacao: 'Outra Situa\u00e7\u00e3o',
    descricao: '\u2014',
    idRegisto: '73182',
    idRegistoOk: '000073182',
    utilizador: 'Rita Cisneiros',
    dataRegisto: 'qui 09/05/2024',
  },
];

interface Vehicle {
  id: number;
  code: string;
  seats: number;
  status: string;
  type: string;
  situation: string;
  location: string;
}

interface VehiclesTabProps {
  onManageVehiclesClick?: () => void;
  canAssign?: boolean;
  onAssignVehicle?: (vehicleId: number) => void;
}

const VehiclesTab = React.memo(function VehiclesTab({
  onManageVehiclesClick,
  canAssign = false,
  onAssignVehicle,
}: VehiclesTabProps) {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [density, setDensity] = useState<DensityMode>('compact');
  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);
  
  // Use our custom animation hook
  const { animateRows, triggerRowAnimations } = useDataGridAnimations();

  const vehicles = useMemo(() => vehiclesMock as Vehicle[], []);
  const scheduledCount = vehicles.filter(
    (vehicle) => vehicle.status === 'Escalada',
  ).length;
  const rowById = useMemo(
    () => new Map(vehicles.map((row) => [row.id, row])),
    [vehicles],
  );
  const allRowIds = useMemo(() => vehicles.map((row) => row.id), [vehicles]);
  const isAllSelected =
    allRowIds.length > 0 && selectionModel.length === allRowIds.length;
  const isIndeterminate =
    selectionModel.length > 0 && selectionModel.length < allRowIds.length;

  const getRowHighlightClass = (row: Vehicle) =>
    row.status === 'Escalada' ? 'row--scheduled' : '';

  const tooltipAnchor = useMemo<VirtualElement | null>(() => {
    if (!tooltipPosition) {
      return null;
    }

    return {
      getBoundingClientRect: () => ({
        x: tooltipPosition.x,
        y: tooltipPosition.y,
        top: tooltipPosition.y,
        left: tooltipPosition.x,
        right: tooltipPosition.x,
        bottom: tooltipPosition.y,
        width: 0,
        height: 0,
        toJSON: () => {},
      }),
    };
  }, [tooltipPosition]);

  const getRowIdFromCell = (cell: HTMLElement) => {
    const rowElement = cell.closest('.MuiDataGrid-row');
    const rowId = rowElement?.getAttribute('data-id');
    if (!rowId) {
      return null;
    }
    const parsedId = Number(rowId);
    return Number.isNaN(parsedId) ? null : parsedId;
  };

  const handleDensityChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDensity: DensityMode | null,
  ) => {
    if (newDensity) {
      setDensity(newDensity);
    }
  };

  const handleAssignVehicle = (vehicleId: number) => {
    if (!canAssign) {
      return;
    }
    if (onAssignVehicle) {
      onAssignVehicle(vehicleId);
      return;
    }
    console.log('Atribuir viatura:', vehicleId);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    rowId: number,
  ) => {
    event.stopPropagation();
    setTooltipPosition(null);
    setMenuAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  const openDriverServiceTotalsModal = (vehicle: Vehicle | undefined) => {
    const modalData: DriverServiceTotalsModalData = {
      driverId: menuRowId ?? 1,
      startDate: '02/05/2024',
      endDate: '08/05/2024',
      filial: 'Transol',
      driver: 'Motorista',
      includeScheduleTime: true,
      footerVariant: 'export',
      driverCost: '0,00',
      services: [],
      totals: [],
      stats: {
        kmsCarga: '1890',
        kmsVazio: '246',
        totalValue: '930,90',
      },
    };

    if (vehicle?.code) {
      modalData.driver = `Motorista (${vehicle.code})`;
    }

    openModal('Servicos e Totais por Motorista', modalData, []);
  };

  const openDriverSituationsModal = (vehicle: Vehicle | undefined) => {
    const modalData: DriverSituationsModalData = {
      driverId: menuRowId ?? 1,
      driverName: vehicle?.code ? `Motorista (${vehicle.code})` : 'Z-Aldina',
      startDate: '01/05/2024',
      endDate: '01/09/2024',
      situacao: 'Todas',
      folgaAtual: 'Segunda-Feira',
      complementarAtual: 'Ter\u00e7a-Feira',
      rows: driverSituationsRows,
    };

    openModal('Situações do Motorista', modalData, []);
  };

  const handleMenuAction = (action: string) => {
    const selectedVehicle =
      menuRowId !== null ? rowById.get(menuRowId) : undefined;
    const targetDriverId = menuRowId ?? selectedVehicle?.id;

    if (action === 'Servi\u00e7os e Totais por Motorista') {
      openDriverServiceTotalsModal(selectedVehicle);
      handleCloseMenu();
      return;
    }

    if (action === 'Situa\u00e7\u00f5es do Motorista') {
      openDriverSituationsModal(selectedVehicle);
      handleCloseMenu();
      return;
    }

    if (
      (action === 'Ficha do Motorista' ||
        action === 'Folha de Ponto Virtual Autom\u00e1tica') &&
      targetDriverId
    ) {
      navigate(`/schedules/driver/${targetDriverId}`);
      handleCloseMenu();
      return;
    }

    console.log('Viatura menu:', action, menuRowId);
    handleCloseMenu();
  };

  const isTooltipBlockedField = (field?: string) =>
    field === '__check__' || field === 'actions' || field === 'select';

  const handleToggleAll = (checked: boolean) => {
    setSelectionModel(checked ? allRowIds : []);
  };

  const handleToggleRow = (rowId: number) => {
    setSelectionModel((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const handleCellMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    const cell = event.currentTarget as HTMLElement;
    const field = cell.dataset.field;
    if (isTooltipBlockedField(field)) {
      setTooltipPosition(null);
      return;
    }

    const rowId = getRowIdFromCell(cell);
    if (rowId === null) {
      return;
    }

    const row = rowById.get(rowId);
    if (!row || !getRowHighlightClass(row)) {
      setTooltipPosition(null);
      return;
    }

    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleCellMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    const cell = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget) {
      const currentRow = cell.closest('.MuiDataGrid-row');
      const nextRow = relatedTarget.closest?.('.MuiDataGrid-row');
      if (
        currentRow &&
        nextRow &&
        currentRow.getAttribute('data-id') ===
          nextRow.getAttribute('data-id')
      ) {
        return;
      }
    }
    setTooltipPosition(null);
  };

  const baseColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'select',
        headerName: '',
        width: 48,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        headerClassName: 'selection-header',
        cellClassName: 'selection-cell',
        renderHeader: () => (
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            size='small'
            color='primary'
            onChange={(event) => handleToggleAll(event.target.checked)}
            inputProps={{ 'aria-label': 'selecionar todos' }}
          />
        ),
        renderCell: (params) => {
          const rowId = Number(params.id);
          const isChecked = selectionModel.includes(rowId);
          return (
            <Checkbox
              checked={isChecked}
              size='small'
              color='primary'
              onClick={(event) => event.stopPropagation()}
              onChange={() => handleToggleRow(rowId)}
              inputProps={{ 'aria-label': 'selecionar linha' }}
            />
          );
        },
      },
      { field: 'code', headerName: 'Viatura', minWidth: 120, flex: 1 },
      { field: 'seats', headerName: 'Lugares', width: 90 },
      { field: 'status', headerName: 'Estado', minWidth: 120, flex: 1 },
      { field: 'type', headerName: 'Tipo', minWidth: 120, flex: 1 },
      {
        field: 'situation',
        headerName: 'Situação',
        minWidth: 140,
        flex: 1,
      },
      { field: 'location', headerName: 'Local', minWidth: 120, flex: 1 },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 72,
        cellClassName: 'actions-cell',
        renderCell: (params) => (
          <Box
            className='row-actions'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              justifyContent: 'flex-end',
              height: '100%',
              opacity: menuRowId === Number(params.id) ? 1 : undefined,
              pointerEvents:
                menuRowId === Number(params.id) ? 'auto' : undefined,
            }}
          >
            <IconButton
              size='small'
              onClick={(event) => {
                event.stopPropagation();
                handleAssignVehicle(Number(params.id));
              }}
              disabled={!canAssign}
              sx={{ padding: 0, width: '10.667px', height: '10.667px' }}
            >
              <ContentPasteOutlinedIcon
                sx={{
                  fontSize: '10.667px',
                  color: canAssign
                    ? '#00A5FF'
                    : 'rgba(134, 149, 175, 0.6)',
                }}
              />
            </IconButton>
            <IconButton
              size='small'
              sx={{ padding: 0, width: 24, height: 24 }}
              onClick={(event) => handleOpenMenu(event, Number(params.id))}
            >
              <MoreVertIcon sx={{ fontSize: 14, color: '#00A5FF' }} />
            </IconButton>
          </Box>
        ),
      },
    ],
    [
      handleAssignVehicle,
      handleOpenMenu,
      isAllSelected,
      isIndeterminate,
      menuRowId,
      selectionModel,
    ],
  );
  const { orderedColumns, handleColumnOrderChange } =
    useColumnOrder(baseColumns);

  const cellPadding = density === 'compact' ? '2px 4px' : '4px 4px';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '12px',
          borderBottom: 'none',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 600,
            fontSize: '16px',
            color: '#092056',
          }}
        >
          {scheduledCount}/{vehicles.length} Viaturas Escaladas
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CustomButton
            variant='text'
            size='small'
            onClick={onManageVehiclesClick}
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 600,
              fontSize: '13px',
              color: '#00A5FF',
              padding: '4px 6px',
              minWidth: 'unset',
              background: 'none',
              '&:hover': {
                background: 'none',
              },
              '&.Mui-focusVisible': {
                background: 'none',
              },
            }}
          >
            Gerir Viaturas
          </CustomButton>
          <IconButton
            size='small'
            onClick={onManageVehiclesClick}
            aria-label='Filtros'
            sx={{ padding: 0 }}
          >
            <FilterAltOutlinedIcon sx={{ color: '#00A5FF', fontSize: 18 }} />
          </IconButton>
          <ToggleButtonGroup
            value={density}
            exclusive
            onChange={handleDensityChange}
            size='small'
            sx={{
              border: '1px solid rgba(134, 149, 175, 0.15)',
              borderRadius: '4px',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: 0,
                padding: '4px',
                '&:not(:last-child)': {
                  borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
                },
                '&.Mui-selected': {
                  backgroundColor: '#f5f7fc',
                  '&:hover': {
                    backgroundColor: '#f5f7fc',
                  },
                },
              },
            }}
          >
            <ToggleButton value='compact' aria-label='densidade compacta'>
              <TableRowsIcon
                sx={{
                  width: '14.4px',
                  height: '14.4px',
                  color:
                    density === 'compact'
                      ? '#00A5FF'
                      : 'rgba(134, 149, 175, 1)',
                }}
              />
            </ToggleButton>
            <ToggleButton value='standard' aria-label='densidade espaçada'>
              <ViewHeadlineIcon
                sx={{
                  width: '14.4px',
                  height: '14.4px',
                  color:
                    density === 'standard'
                      ? '#00A5FF'
                      : 'rgba(134, 149, 175, 1)',
                }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, marginTop: '12px' }}>
        <AnimatedDataGridPro
          rows={vehicles}
          columns={orderedColumns}
          onColumnOrderChange={handleColumnOrderChange}
          density={density}
          animateRows={animateRows}
          autoHeight={false}
          columnHeaderHeight={density === 'compact' ? 36 : 44}
          disableRowSelectionOnClick
          getRowClassName={(params) => getRowHighlightClass(params.row)}
          slots={{ columnMenu: CustomColumnMenu }}
          slotProps={{
            cell: {
              onMouseEnter: handleCellMouseEnter,
              onMouseLeave: handleCellMouseLeave,
            },
            basePopper: {
              sx: columnMenuPopperSx,
            },
          }}
          sx={[
            {
              border: 'none',
              '--DataGrid-rowBorderColor': 'transparent',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#FFFFFF',
                borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
                borderTop: 'none',
                boxShadow: 'none',
              },
              '& .selection-cell, & .selection-header': {
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '12px',
                paddingRight: '4px',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: '12px',
                color: '#8695AF',
                textTransform: 'uppercase',
              },
              '& .MuiDataGrid-columnSeparator': {
                opacity: 0,
                color: 'rgba(134, 149, 175, 0.6)',
                transition: 'opacity 0.2s ease',
              },
              '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
                opacity: 1,
              },
              '& .MuiDataGrid-columnSeparator--resizable': {
                cursor: 'col-resize',
              },
              '& .MuiDataGrid-columnHeader': {
                borderRight: 'none',
              },
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                padding: cellPadding,
              },
              '& .MuiDataGrid-cell': {
                fontFamily: 'Poppins',
                fontSize: '13px',
                color: '#092056',
                borderBottom: 'none',
                borderRight: 'none',
              },
              '& .MuiDataGrid-row': {
                borderBottom: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 165, 255, 0.06)',
              },
              '& .row--scheduled': {
                backgroundColor: 'rgba(0, 165, 255, 0.08)',
              },
              '& .MuiDataGrid-cell.actions-cell': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              },
              '& .row-actions': {
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease',
              },
              '& .MuiDataGrid-row:hover .row-actions': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            },
          ]}
        />
      </Box>
      <Tooltip
        open={Boolean(tooltipAnchor && !menuAnchorEl)}
        placement='bottom-start'
        title={
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 500,
              fontSize: '10px',
              lineHeight: '14px',
              color: '#092056',
              whiteSpace: 'nowrap',
            }}
          >
            Confirme as Observações
          </Typography>
        }
        PopperProps={{
          anchorEl: tooltipAnchor,
        }}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#FFFFFF',
              borderRadius: '4px',
              pointerEvents: 'none',
              padding: '10px',
              boxShadow:
                '0px 1px 10px 0px rgba(9, 32, 86, 0.12), 0px 4px 5px 0px rgba(9, 32, 86, 0.14), 0px 2px 4px -1px rgba(9, 32, 86, 0.2)',
              maxWidth: 'none',
            },
          },
        }}
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
        <Box sx={{ width: 0, height: 0 }} />
      </Tooltip>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderRadius: '4px',
            boxShadow:
              '0px 3px 14px 2px rgba(9, 32, 86, 0.12), 0px 8px 10px 1px rgba(9, 32, 86, 0.14), 0px 5px 5px -3px rgba(9, 32, 86, 0.2)',
            minWidth: '257px',
          },
        }}
        MenuListProps={{
          sx: {
            padding: '8px 0',
          },
        }}
      >
        {vehicleMenuItems.map((item, index) => (
          <React.Fragment key={item}>
            {index === 2 && <Divider sx={{ borderColor: 'rgba(9, 32, 86, 0.12)' }} />}
            <MenuItem
              onClick={() => handleMenuAction(item)}
              sx={{
                padding: '6px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: 1.5,
                  color: '#092056',
                }}
              >
                {item}
              </Typography>
              <KeyboardArrowRightRoundedIcon
                sx={{ fontSize: 18, color: '#00A5FF', flexShrink: 0 }}
              />
            </MenuItem>
          </React.Fragment>
        ))}
      </Menu>
    </Box>
  );
});

export default VehiclesTab;

